// DocumentHighlightRow — the visual body of one search hit (CTSK-494
// Phase 1c extraction of Phase 1b's DocumentSearchResult, design doc §3.3):
//
//   [BI] Title with <mark>hits</mark>                                 ← title
//        …body snippet with <mark>hits</mark>…                        ← snippet (skipped when "")
//        adr · nests · templates +2            updated 2026-09-25     ← tags + date
//
// Pure render, no interactive wrapper: the dropdown's DocumentSearchResult
// wraps it in a `role="option"` div (combobox semantics, focus ring, the
// mousedown-eat trick) and the Documents page's search mode wraps it in a
// plain link row. One body, two shells — a highlight bug is fixed once.
//
// Phase 1a real-data cases (CTSK-493 brief §2.2): `snippet === ""` → no
// snippet line; `nest === null` → no badge. Covered in
// DocumentSearchResult.test.tsx (through the option shell) and
// DocumentLibrary.search-mode.test.tsx (through the list shell).

import type { DocumentSearchResult as SearchResult } from '@captain-tasko/shared';
import { snippetSegments, titleSegments, type TextSegment } from './highlight.js';
import { labelChipsFor, type LabelKind } from './labelChips.js';

export interface DocumentHighlightRowProps {
  result: SearchResult;
  /** Hide the nest badge (the row sits under a labelled group header). */
  hideNestBadge?: boolean;
  /** `full` lets the snippet wrap instead of truncating to one line. */
  variant?: 'compact' | 'full';
  /**
   * CTSK-507 — the group / collection / folder chips (shown only for labels the
   * result carries). With a handler each chip is a button that adds that filter
   * (it sends the STORED value); without one the chips are plain text.
   */
  onLabelFilter?: (kind: LabelKind, value: string) => void;
  /**
   * CTSK-521 — the Documents page's column picker hides these two meta items
   * per operator. Both default on, so the header dropdown is unchanged.
   */
  showTags?: boolean;
  showUpdated?: boolean;
}

export const MAX_INLINE_TAGS = 3;

function Segments({ segments }: { segments: TextSegment[] }) {
  return (
    <>
      {segments.map((s, i) =>
        s.marked ? <mark key={i}>{s.text}</mark> : <span key={i}>{s.text}</span>,
      )}
    </>
  );
}

/** Same convention as the Documents list (`toLocaleDateString`). */
export function formatUpdated(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export function DocumentHighlightRow({
  result,
  hideNestBadge = false,
  variant = 'compact',
  onLabelFilter,
  showTags = true,
  showUpdated = true,
}: DocumentHighlightRowProps) {
  const title = titleSegments(result.title, result.titleHighlights);
  const snippet = snippetSegments(result.snippet);
  const tags = result.tags ?? [];
  const shownTags = tags.slice(0, MAX_INLINE_TAGS);
  const extraTags = tags.length - shownTags.length;
  const updated = formatUpdated(result.updatedAt);
  const full = variant === 'full';
  const chips = labelChipsFor(result);

  return (
    <>
      <div className="doc-search__row">
        {result.nest && !hideNestBadge && (
          <span
            className="doc-search__badge"
            title={result.nest.name}
            aria-label={`Nest ${result.nest.name}`}
          >
            {result.nest.code}
          </span>
        )}
        <span className={`doc-search__title${full ? ' doc-search__title--full' : ''}`}>
          <Segments segments={title} />
        </span>
      </div>
      {snippet.length > 0 && (
        <div
          className={`doc-search__snippet${full ? ' doc-search__snippet--full' : ''}`}
          data-testid="document-search-snippet"
        >
          <Segments segments={snippet} />
        </div>
      )}
      <div className="doc-search__meta">
        {chips.length > 0 && (
          <span className="doc-search__labels" data-testid="document-search-label-chips">
            {chips.map((c) =>
              onLabelFilter ? (
                <button
                  key={c.kind}
                  type="button"
                  className={`doc-search__chip doc-search__chip--${c.kind}`}
                  data-kind={c.kind}
                  title={c.title}
                  // The dropdown keeps the pointer for the input (no tab stop); the page row is a normal control.
                  tabIndex={full ? 0 : -1}
                  onClick={(e) => {
                    // The row is a link (page) or an option (dropdown): a chip click is
                    // its own action and must not also open the document.
                    e.preventDefault();
                    e.stopPropagation();
                    onLabelFilter(c.kind, c.value);
                  }}
                >
                  {c.text}
                </button>
              ) : (
                <span key={c.kind} className={`doc-search__chip doc-search__chip--${c.kind}`} data-kind={c.kind} title={c.title}>
                  {c.text}
                </span>
              ),
            )}
          </span>
        )}
        {showTags && <span className="doc-search__tags">
          {shownTags.map((t) => (
            <span key={t} className="doc-search__tag">{t}</span>
          ))}
          {extraTags > 0 && (
            <span className="doc-search__tag doc-search__tag--more" title={tags.slice(MAX_INLINE_TAGS).join(', ')}>
              +{extraTags}
            </span>
          )}
        </span>}
        {showUpdated && updated && (
          <span className="doc-search__updated">
            <span className="sr-only">updated </span>
            <time dateTime={result.updatedAt}>{updated}</time>
          </span>
        )}
      </div>
    </>
  );
}
